'use client';

import React, { useCallback, useState, JSX } from 'react';
import { IsoDropdown, useAppService, useConfig } from '@ibe/components';
import { getKlaroConfig } from '@/Tracking/setup';
import isClient from '@/Util/globals';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { Language } from '@/app/i18n/settingsClient';
import { useTranslation } from '@/app/i18n/client';

const LangSwitchDrop = ({
  handleLanguageChange,
  languageOptions
}: {
  handleLanguageChange: (lang: string) => Promise<void>;
  languageOptions: { label: string; value: Language }[];
}): JSX.Element => {
  const { i18n } = useTranslation('klaro');
  const appService = useAppService();
  const config = useConfig();

  const [languageDropDownOpen, setLanguageDropDownOpen] = useState<boolean>(false);

  const toggleLanguageDropDown = useCallback(
    (): void => setLanguageDropDownOpen(prevState => !prevState),
    []
  );

  const onLanguageChange = useCallback(
    async (value: string): Promise<void> => {
      toggleLanguageDropDown();
      await handleLanguageChange(value);
    },
    [handleLanguageChange]
  );

  const updateKlaroConfig = (lang: string) => {
    if (config.useConsentManager && isClient()) {
      if (window.klaroConfig) window.klaroConfig = getKlaroConfig(i18n, lang);
      if (window.klaro) window.klaro.setup(getKlaroConfig(i18n, lang));
    }
  };

  return (
    <div className="d-flex language-dropdown">
      <div className="headerSelectionLayout text-nowrap">
        <span>
          {' '}
          <IsoDropdown
            isOpen={languageDropDownOpen}
            onDropdownToggle={setLanguageDropDownOpen}
            title={
              <div className="text-uppercase">
                {appService.lang.substring(0, 2)}&nbsp;
                {languageDropDownOpen ? (
                  <FontAwesomeIcon icon={faChevronUp} />
                ) : (
                  <FontAwesomeIcon icon={faChevronDown} />
                )}
              </div>
            }
          >
            {languageOptions
              .filter(lang => lang.value !== appService.lang)
              .map(option => (
                <div
                  className="text-center dropdown-item p-0"
                  key={`lang_${option.value}`}
                  onClick={(): void => {
                    onLanguageChange(option.value);
                    updateKlaroConfig(option.value);
                  }}
                  onKeyDown={(event): void => {
                    event.preventDefault();
                    if (event.keyCode === 13) {
                      onLanguageChange(option.value);
                      updateKlaroConfig(option.value);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  {option?.label?.toUpperCase()}
                </div>
              ))}
          </IsoDropdown>
        </span>
      </div>
    </div>
  );
};

export default LangSwitchDrop;
